import { CollisionDetection, DroppableContainer } from '@dnd-kit/core';

interface ItemWithCenter {
  id: string | number;
  centerY: number;
  index: number;
}

/**
 * Find the furthest item crossed when dragging down (to higher indices)
 */
const findTargetWhenDraggingDown = (
  items: ItemWithCenter[],
  draggedCenterY: number,
  activeIndex: number
): ItemWithCenter | null => {
  let targetItem: ItemWithCenter | null = null;

  for (const item of items) {
    if (item.index > activeIndex && draggedCenterY >= item.centerY) {
      targetItem = item;
    } else if (item.index > activeIndex) {
      break;
    }
  }

  return targetItem;
};

/**
 * Find the furthest item crossed when dragging up (to lower indices)
 */
const findTargetWhenDraggingUp = (
  items: ItemWithCenter[],
  draggedCenterY: number,
  activeIndex: number
): ItemWithCenter | null => {
  let targetItem: ItemWithCenter | null = null;

  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.index < activeIndex && draggedCenterY <= item.centerY) {
      targetItem = item;
    } else if (item.index < activeIndex) {
      break;
    }
  }

  return targetItem;
};

/**
 * Extract items with their center positions from droppable containers
 */
const getItemsWithCenters = (
  droppableContainers: DroppableContainer[],
  activeId: string | number
): ItemWithCenter[] => {
  return droppableContainers
    .map((container) => {
      if (container.id === activeId) return null;

      const droppableRect = container.rect.current;
      if (!droppableRect) return null;

      const centerY = droppableRect.top + droppableRect.height / 2;
      const sortableData = container.data.current?.sortable as { index?: number } | undefined;
      const index = sortableData?.index;

      if (index === undefined) return null;

      return { id: container.id, centerY, index };
    })
    .filter((item): item is ItemWithCenter => item !== null)
    .sort((a, b) => a.index - b.index);
};

/**
 * Simple center-based collision detection algorithm.
 *
 * This algorithm compares the center Y position of the dragged item with the center Y
 * positions of all droppable items. It only triggers a swap when the dragged item's
 * center crosses the center of another item.
 *
 * ## How it works:
 * 1. Calculate the center Y position of the dragged item
 * 2. For each droppable item, calculate its center Y position
 * 3. Find the closest item whose center has been crossed:
 *    - If dragging DOWN: Find items whose center is ABOVE the dragged center
 *    - If dragging UP: Find items whose center is BELOW the dragged center
 * 4. Return the closest crossed item, or empty array if no center has been crossed
 *
 * ## Why this approach:
 * - **Simple and predictable**: Easy to understand and debug
 * - **Prevents premature swaps**: Only swaps when you cross the center of an item
 * - **Supports multi-position jumps**: Can jump from position 1 to 3 naturally
 * - **No complex calculations**: Just center position comparisons
 * - **Stable dragging**: Small movements don't trigger position changes
 *
 * @returns A collision detection function compatible with dnd-kit's DndContext
 */
export const createOverlapCollisionDetection = (): CollisionDetection => {
  return (args) => {
    const { collisionRect, droppableContainers, active } = args;

    const draggedCenterY = collisionRect.top + collisionRect.height / 2;
    const activeSortableData = active.data.current?.sortable as { index?: number } | undefined;
    const activeIndex = activeSortableData?.index;

    if (activeIndex === undefined) {
      return [];
    }

    const itemsWithCenters = getItemsWithCenters(Array.from(droppableContainers), active.id);

    // Try dragging down first, then up
    const targetItem =
      findTargetWhenDraggingDown(itemsWithCenters, draggedCenterY, activeIndex) ??
      findTargetWhenDraggingUp(itemsWithCenters, draggedCenterY, activeIndex);

    return targetItem ? [{ id: targetItem.id }] : [];
  };
};
